import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtDate, fmtHours } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Avatar } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  FileText, ShieldCheck, CheckCircle2, FolderOpen, Clock, Mail, Phone, MapPin,
  UserCircle, Briefcase, Pencil, CalendarClock, GraduationCap, TrendingUp, LogOut, Flag, ListChecks, Wallet,
} from "lucide-react";
import { toast } from "sonner";

const TL_ICON = {
  hire: Flag, job: Briefcase, salary: Wallet, review: TrendingUp,
  training: GraduationCap, leaver: LogOut, onboarding: ListChecks, offboarding: ListChecks,
};
const TL_TONE = {
  hire: "bg-[#2EA3FD]", job: "bg-violet-500", salary: "bg-emerald-500", review: "bg-amber-500",
  training: "bg-teal-500", leaver: "bg-rose-500", onboarding: "bg-slate-400", offboarding: "bg-slate-400",
};

function tenure(start) {
  if (!start) return "—";
  const s = new Date(start), n = new Date();
  let months = (n.getFullYear() - s.getFullYear()) * 12 + (n.getMonth() - s.getMonth());
  if (n.getDate() < s.getDate()) months -= 1;
  if (months < 0) months = 0;
  const y = Math.floor(months / 12), m = months % 12;
  return `${y ? `${y}y ` : ""}${m}m`;
}

function MetricTile({ label, value, sub, accent = "blue", testid }) {
  const tone = { blue: "border-t-[#2EA3FD] text-[#2EA3FD]", teal: "border-t-[#36776B] text-[#36776B]", navy: "border-t-[#0B1A2F] text-[#0B1A2F]", amber: "border-t-amber-400 text-amber-600" }[accent];
  return (
    <div data-testid={testid} className={`bg-white border border-slate-200 border-t-4 ${tone.split(" ")[0]} rounded-xl shadow-sm p-5`}>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      <p className={`text-2xl font-extrabold font-display mt-1 ${tone.split(" ")[1]}`}>{value}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
    </div>
  );
}

function Card({ title, icon: Icon, children, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm" data-testid={testid}>
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2"><Icon size={16} className="text-[#2EA3FD]" /><h3 className="font-semibold text-[#0B1A2F] text-sm">{title}</h3></div>
      <div className="divide-y divide-slate-50">{children}</div>
    </div>
  );
}

export default function MyHR() {
  const { user, refreshUser } = useAuth();
  const [emp, setEmp] = useState(null);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [docs, setDocs] = useState([]);
  const [policies, setPolicies] = useState([]);
  const [sections, setSections] = useState([]);

  const load = () => {
    api.get("/policies").then((r) => setPolicies(r.data.filter((p) => p.in_scope))).catch(() => {});
    api.get("/sections").then((r) => setSections(r.data)).catch(() => {});
    const id = user?.employee_id;
    if (id) {
      api.get(`/employees/${id}`).then((r) => setEmp(r.data)).catch(() => {});
      api.get(`/employees/${id}/summary`).then((r) => setSummary(r.data)).catch(() => {});
      api.get(`/employees/${id}/timeline`).then((r) => setTimeline(r.data)).catch(() => {});
      api.get(`/employees/${id}/documents`).then((r) => setDocs(r.data)).catch(() => setDocs([]));
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user]);

  const openFile = async (url) => { try { const r = await api.get(url, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); } catch { toast.error("Not available"); } };
  const ack = async (id) => { try { await api.post(`/policies/${id}/ack`); toast.success("Acknowledged"); load(); } catch { toast.error("Could not acknowledge"); } };

  const saveName = async () => {
    const v = nameDraft.trim();
    if (v.length < 2) { toast.error("Please enter your name"); return; }
    setSavingName(true);
    try { await api.post("/auth/update-profile", { name: v }); await refreshUser(); setEditingName(false); toast.success("Name updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update name"); }
    setSavingName(false);
  };

  const sectionName = (id) => sections.find((s) => s.id === id)?.name || "General";
  const toAck = policies.filter((p) => p.requires_ack && !p.acknowledged);
  const archiveItems = [
    ...policies.filter((p) => !p.requires_ack || p.acknowledged).map((p) => ({ ...p, _kind: "policy" })),
    ...docs.map((d) => ({ ...d, _kind: "doc" })),
  ];
  const grouped = archiveItems.reduce((acc, it) => { const key = sectionName(it.section_id); (acc[key] = acc[key] || []).push(it); return acc; }, {});
  const groupNames = Object.keys(grouped).sort();

  const name = emp ? `${emp.first_name} ${emp.last_name}` : user?.name || "";
  const jobTitle = emp?.job_title || (emp?.department ? `${emp.department} team` : "Team member");

  return (
    <div className="space-y-6" data-testid="my-profile-page">
      {/* Profile header */}
      <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden" data-testid="profile-header">
        <div className="h-20 bg-gradient-to-r from-[#0B1A2F] via-[#0F2C50] to-[#2EA3FD]" />
        <div className="px-6 pb-6 -mt-10">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div className="flex items-end gap-4 min-w-0">
              <Avatar name={name} photo={emp?.photo_url} size={88} className="ring-4 ring-white !shadow-md" />
              <div className="min-w-0 pb-1">
                {editingName ? (
                  <div className="flex flex-wrap items-center gap-2">
                    <Input value={nameDraft} onChange={(e) => setNameDraft(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveName(); }} className="h-9 w-60" data-testid="profile-name-input" autoFocus />
                    <Button size="sm" onClick={saveName} disabled={savingName} className="bg-[#0B1A2F] hover:bg-[#162B48]" data-testid="profile-name-save">{savingName ? "Saving…" : "Save"}</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditingName(false)} data-testid="profile-name-cancel">Cancel</Button>
                  </div>
                ) : (
                  <h1 className="text-2xl sm:text-3xl font-extrabold font-display text-white truncate [text-shadow:0_1px_4px_rgba(0,0,0,0.45)]" data-testid="profile-name">{name}</h1>
                )}
                <p className="text-sm text-slate-500 flex items-center gap-1.5 mt-0.5" data-testid="profile-jobtitle"><Briefcase size={14} className="text-[#2EA3FD]" />{jobTitle}{emp?.department ? ` · ${emp.department}` : ""}</p>
                {emp?.employee_code && <span className="inline-block mt-1.5 text-[11px] font-semibold uppercase tracking-wide bg-slate-100 text-slate-500 rounded-full px-2.5 py-0.5">{emp.employee_code}</span>}
              </div>
            </div>
            {user?.employee_id ? (
              <Button asChild variant="outline" className="shrink-0" data-testid="profile-edit-btn"><Link to={`/employees/${user.employee_id}`}><Pencil size={15} className="mr-1.5" />Edit my details</Link></Button>
            ) : !editingName ? (
              <Button variant="outline" className="shrink-0" data-testid="profile-edit-name-btn" onClick={() => { setNameDraft(user?.name || ""); setEditingName(true); }}><Pencil size={15} className="mr-1.5" />Edit name</Button>
            ) : null}
          </div>
          {/* Contact chips */}
          <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5 text-sm" data-testid="profile-contact">
            {emp?.email && <a href={`mailto:${emp.email}`} className="flex items-center gap-2 text-slate-600 hover:text-[#2EA3FD]"><Mail size={15} className="text-slate-400" />{emp.email}</a>}
            {emp?.phone && <span className="flex items-center gap-2 text-slate-600"><Phone size={15} className="text-slate-400" />{emp.phone}</span>}
            {emp?.address && <span className="flex items-center gap-2 text-slate-600"><MapPin size={15} className="text-slate-400" />{emp.address}</span>}
            {emp?.start_date && <span className="flex items-center gap-2 text-slate-600"><CalendarClock size={15} className="text-slate-400" />Joined {fmtDate(emp.start_date)}</span>}
          </div>
        </div>
      </div>

      {/* Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="profile-overview">
        <MetricTile testid="overview-available" label="Holiday available" value={summary ? fmtHours(summary.confirmed_remaining) : "—"} sub={summary?.holiday_year?.name} accent="blue" />
        <MetricTile testid="overview-booked" label="Taken / booked" value={summary ? fmtHours(summary.total_hours_booked) : "—"} sub="This leave year" accent="teal" />
        <MetricTile testid="overview-entitlement" label="Total entitlement" value={summary ? fmtHours(summary.total_entitlement) : "—"} sub="Including adjustments" accent="navy" />
        <MetricTile testid="overview-tenure" label="Time at CASS" value={tenure(emp?.start_date)} sub={summary?.next_booked ? `Next off ${fmtDate(summary.next_booked)}` : "Length of service"} accent="amber" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Employment journey timeline */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl shadow-sm" data-testid="profile-timeline">
          <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2"><CalendarClock size={16} className="text-[#2EA3FD]" /><h3 className="font-semibold text-[#0B1A2F] text-sm">My employment journey</h3></div>
          <div className="p-5">
            {timeline.length === 0 ? <p className="text-sm text-slate-400 py-4">Your journey will appear here as milestones are recorded.</p> : (
              <ol className="relative border-l-2 border-slate-100 ml-2 space-y-5">
                {timeline.map((e, i) => {
                  const Icon = TL_ICON[e.type] || Flag;
                  return (
                    <li key={i} className="ml-6" data-testid={`timeline-event-${i}`}>
                      <span className={`absolute -left-[13px] grid place-items-center h-6 w-6 rounded-full text-white ${TL_TONE[e.type] || "bg-slate-400"}`}><Icon size={12} /></span>
                      <div className="flex items-baseline justify-between gap-3 flex-wrap">
                        <p className="text-sm font-semibold text-[#0B1A2F]">{e.title}</p>
                        <span className="text-xs text-slate-400">{fmtDate(e.date)}</span>
                      </div>
                      {e.detail && <p className="text-xs text-slate-500 mt-0.5">{e.detail}</p>}
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </div>

        {/* To acknowledge */}
        <div className="space-y-6">
          <Card title={`To acknowledge${toAck.length ? ` (${toAck.length})` : ""}`} icon={Clock} testid="myhr-to-acknowledge">
            {toAck.length === 0 ? <p className="text-sm text-slate-400 p-5">You're all caught up — nothing awaiting your acknowledgement.</p> :
              toAck.map((p) => (
                <div key={p.id} className="flex items-center justify-between gap-2 px-5 py-3 text-sm" data-testid={`myhr-pending-${p.id}`}>
                  <button onClick={() => openFile(`/documents/${p.id}/file`)} className="text-left min-w-0">
                    <span className="font-medium text-[#2EA3FD] hover:underline truncate">{p.title}</span>
                    <p className="text-xs text-amber-600">{sectionName(p.section_id)} · {p.ack_due_date ? (p.ack_due_date < new Date().toISOString().slice(0, 10) ? `Overdue (was due ${p.ack_due_date})` : `Due by ${p.ack_due_date}`) : "Awaiting your acknowledgement"}</p>
                  </button>
                  <Button size="sm" className="bg-amber-500 hover:bg-amber-600 shrink-0" onClick={() => ack(p.id)} data-testid={`myhr-ack-${p.id}`}>Acknowledge</Button>
                </div>
              ))}
          </Card>
        </div>
      </div>

      {/* Documents & acknowledged policies */}
      <div>
        <h3 className="font-semibold text-[#0B1A2F] mb-3 flex items-center gap-2"><FolderOpen size={17} className="text-[#2EA3FD]" />My documents &amp; acknowledged policies</h3>
        {groupNames.length === 0 ? <p className="text-sm text-slate-400">Nothing filed yet.</p> : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {groupNames.map((gname) => (
              <Card key={gname} title={gname} icon={gname === "General" ? FileText : ShieldCheck} testid={`myhr-section-${gname.replace(/\s+/g, "-").toLowerCase()}`}>
                {grouped[gname].map((it) => (
                  <button key={`${it._kind}-${it.id}`} onClick={() => openFile(`/documents/${it.id}/file`)} className="w-full flex items-center justify-between gap-2 px-5 py-3 text-sm hover:bg-slate-50 text-left" data-testid={`myhr-item-${it.id}`}>
                    <span className="min-w-0">
                      <span className="font-medium text-[#0B1A2F] truncate">{it.title}</span>
                      {it._kind === "policy" && it.acknowledged && <span className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle2 size={12} />Acknowledged {it.acknowledged_at ? fmtDate(it.acknowledged_at) : ""}</span>}
                      {it._kind === "policy" && !it.requires_ack && <span className="text-xs text-slate-400">Company policy · info only</span>}
                      {it._kind === "doc" && <span className="text-xs text-slate-400">{it.filename}</span>}
                    </span>
                    <span className="text-[10px] uppercase tracking-wide text-slate-400 shrink-0">{it._kind === "policy" ? "Policy" : "Document"}</span>
                  </button>
                ))}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
